import * as signalR from '@microsoft/signalr';
import { store } from '../store';
import { commentReceived, connectionStatusChanged } from '../store/slices/techSupportChatSlice';
import toastService from '../utils/toastService';
import { getEnv } from '../config/runtimeConfig';

class TechSupportChatService {
    constructor() {
        this.connection = null;
        this.currentTicketId = null;
        this._onCommentReceivedCallbacks = [];
    }

    onCommentReceived(callback) {
        this._onCommentReceivedCallbacks.push(callback);
        return () => {
            this._onCommentReceivedCallbacks = this._onCommentReceivedCallbacks.filter(cb => cb !== callback);
        };
    }

    _normalizeComment(raw) {
        return {
            ticketId: raw.ticketId ?? raw.TicketId ?? this.currentTicketId,
            commentId: raw.commentId ?? raw.CommentId ?? raw.id ?? raw.Id,
            id: raw.id ?? raw.Id ?? raw.commentId ?? raw.CommentId,
            createdBy: raw.createdBy ?? raw.CreatedBy ?? raw.createdUserId ?? raw.CreatedUserId,
            userName: raw.userName ?? raw.UserName ?? raw.createdName ?? raw.CreatedName ?? 'Unknown',
            message: raw.message ?? raw.Message ?? raw.comment ?? raw.Comment ?? '',
            timestamp: raw.timestamp ?? raw.Timestamp ?? raw.createdDate ?? raw.CreatedDate ?? new Date().toISOString(),
            type: raw.type ?? raw.Type ?? 1
        };
    }

    async connect() {
        if (this.connection?.state === signalR.HubConnectionState.Connected) return;

        if (this.connection) {
            await this.disconnect();
        }

        const apiUrl = (getEnv('REACT_APP_API_URL') || 'https://localhost:7149').replace(/\/$/, '');

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(`${apiUrl}/hubs/techsupport-chat`, {
                withCredentials: true,
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(
                getEnv('REACT_APP_ENABLE_DEBUG_LOGGING') === 'true'
                    ? signalR.LogLevel.Information
                    : signalR.LogLevel.Warning
            )
            .build();

        this.connection.on('ReceiveComment', (raw) => {
            const normalized = this._normalizeComment(raw);
            const currentUserId = store.getState()?.userParams?.userId;

            if (String(normalized.createdBy) !== String(currentUserId)) {
                store.dispatch(commentReceived(normalized));
                this._onCommentReceivedCallbacks.forEach(cb => cb(normalized));
                toastService.info('Yeni mesaj alındı', 'Chat');
            }
        });

        this.connection.onreconnecting(() => {
            store.dispatch(connectionStatusChanged(false));
        });

        this.connection.onreconnected(() => {
            store.dispatch(connectionStatusChanged(true));
            if (this.currentTicketId) {
                this.joinTicketGroup(this.currentTicketId);
            }
        });

        this.connection.onclose(() => {
            store.dispatch(connectionStatusChanged(false));
        });

        try {
            await this.connection.start();
            store.dispatch(connectionStatusChanged(true));
        } catch (err) {
            console.error('SignalR connection failed:', err);
            store.dispatch(connectionStatusChanged(false));
        }
    }

    async joinTicketGroup(ticketId) {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            await this.connect();
        }

        try {
            this.currentTicketId = ticketId;
            await this.connection.invoke('JoinTicketGroup', ticketId);
        } catch (err) {
            console.error(`Failed to join ticket group ${ticketId}:`, err);
        }
    }

    async leaveTicketGroup(ticketId) {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) return;

        try {
            await this.connection.invoke('LeaveTicketGroup', ticketId);
            this.currentTicketId = null;
        } catch (err) {
            console.error(`Failed to leave ticket group ${ticketId}:`, err);
        }
    }

    async disconnect() {
        if (this.connection) {
            try {
                await this.connection.stop();
            } catch (err) {
                console.error('SignalR disconnect error:', err);
            }
            this.connection = null;
            this.currentTicketId = null;
            store.dispatch(connectionStatusChanged(false));
        }
    }
}

export const techSupportChatService = new TechSupportChatService();
